/**
 * OPS Direct-Retrieval service client.
 * Wraps /api/ops-retrieval/* endpoints.
 */

import apiClient from './api';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface OpsAdvancedQuery {
  keywords_title_abstract?: string;
  keywords_title?: string;
  keywords_full_text?: string;
  inventor?: string[];
  applicant?: string[];
  cpc?: string[];
  ipc?: string[];
  publication_number?: string;
  application_number?: string;
  priority_number?: string;
  publication_date_from?: string;
  publication_date_to?: string;
  application_date_from?: string;
  application_date_to?: string;
  priority_date_from?: string;
  priority_date_to?: string;
  country_codes?: string[];
  language?: string;
}

export interface OpsPreviewSample {
  document_id: string;
  title: string;
  abstract_excerpt?: string;
  cpc_codes?: string[];
}

/** Result from the fast /preview/count endpoint (no samples). */
export interface OpsPreviewCountResult {
  total_count: number;
  effective_count: number;
  cql: string;
  term_count: number;
  warnings: string[];
  estimated_list_seconds: number;
  estimated_full_seconds: number;
  estimated_full_google_seconds: number;
}

/** Full preview result (with samples) — used by legacy endpoint and assembled client-side. */
export interface OpsPreviewResult {
  total_count: number;
  effective_count: number;
  sample: OpsPreviewSample[];
  cql: string;
  term_count: number;
  warnings: string[];
  estimated_list_seconds: number;
  estimated_full_seconds: number;
  estimated_full_google_seconds: number;
}

export interface OpsJobProgress {
  total_patents: number;
  processed_patents: number;
  failed_patents: number;
  google_patents_failures: number;
  current_patent_id: string | null;
}

export interface OpsRetrievalJob {
  id: string;
  search_session_id: string;
  sub_project_id: string;
  user_id: string;
  target_kb_id: string | null;
  cql: string;
  mode: 'list' | 'full';
  text_source: 'google_patents' | 'ops';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'cancelling';
  progress: OpsJobProgress;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error_details: string | null;
  estimated_duration_seconds: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const opsRetrievalService = {
  /** Fast count-only preview (≤15s typical, 25s timeout). */
  async previewCount(query: OpsAdvancedQuery): Promise<OpsPreviewCountResult> {
    const response = await apiClient.post<OpsPreviewCountResult>(
      '/ops-retrieval/preview/count',
      { query },
      { timeout: 25000 },
    );
    return response.data;
  },

  /**
   * Stream sample biblio via SSE. Calls onSample for each sample as it arrives.
   * Returns an AbortController so the caller can cancel the stream.
   */
  streamSamples(
    cql: string,
    count: number,
    onSample: (sample: OpsPreviewSample) => void,
    onDone: () => void,
    onError: (message: string) => void,
  ): AbortController {
    const controller = new AbortController();
    const token = sessionStorage.getItem('access_token');
    const baseURL = apiClient.defaults.baseURL || '/api';

    // Overall 60s timeout
    const timeoutId = setTimeout(() => {
      controller.abort();
      onError('Sample loading timed out (60s)');
    }, 60000);

    (async () => {
      try {
        const response = await fetch(`${baseURL}/ops-retrieval/preview/samples`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ cql, count }),
          signal: controller.signal,
        });

        if (!response.ok) {
          clearTimeout(timeoutId);
          onError(`Server error: ${response.status}`);
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          clearTimeout(timeoutId);
          onError('No response body');
          return;
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEvent = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              const data = line.slice(6);
              try {
                const parsed = JSON.parse(data);
                if (currentEvent === 'sample') {
                  onSample(parsed as OpsPreviewSample);
                } else if (currentEvent === 'done') {
                  clearTimeout(timeoutId);
                  onDone();
                  return;
                } else if (currentEvent === 'error') {
                  clearTimeout(timeoutId);
                  onError(parsed.message || 'Unknown error');
                  return;
                }
              } catch {
                // Skip malformed JSON
              }
              currentEvent = '';
            }
          }
        }

        clearTimeout(timeoutId);
        onDone();
      } catch (err: unknown) {
        clearTimeout(timeoutId);
        const e = err as { name?: string; message?: string };
        if (e.name === 'AbortError') return; // Intentional abort
        onError(e?.message || 'Stream failed');
      }
    })();

    return controller;
  },

  /** Legacy: synchronous count + 5 sample biblio rows (now has 90s server timeout). */
  async preview(query: OpsAdvancedQuery): Promise<OpsPreviewResult> {
    const response = await apiClient.post<OpsPreviewResult>(
      '/ops-retrieval/preview',
      { query },
      { timeout: 95000 },
    );
    return response.data;
  },

  /** Phase B — create and start a background retrieval job. */
  async startJob(params: {
    sub_project_id: string;
    query: OpsAdvancedQuery;
    mode: 'list' | 'full';
    text_source?: 'google_patents' | 'ops';
    target_kb_id?: string;
    new_kb_name?: string;
    cql?: string;
    total_count?: number;
    /** Optional cap: download at most this many patents (quick-test mode). */
    max_patents?: number | null;
  }): Promise<OpsRetrievalJob> {
    const response = await apiClient.post<OpsRetrievalJob>('/ops-retrieval/jobs', params);
    return response.data;
  },

  /** Test Google Patents connectivity with a sample patent. */
  async testGooglePatents(patentId: string): Promise<{ available: boolean; response_time_ms: number; error: string | null }> {
    const response = await apiClient.post<{ available: boolean; response_time_ms: number; error: string | null }>(
      '/ops-retrieval/test-google-patents',
      { patent_id: patentId },
      { timeout: 35000 },
    );
    return response.data;
  },

  /** Poll job status. */
  async getJobStatus(jobId: string): Promise<OpsRetrievalJob> {
    const response = await apiClient.get<OpsRetrievalJob>(`/ops-retrieval/jobs/${jobId}`);
    return response.data;
  },

  /** Request cooperative cancellation. */
  async cancelJob(jobId: string): Promise<void> {
    await apiClient.post(`/ops-retrieval/jobs/${jobId}/cancel`);
  },

  /** Re-link all documents from a completed job to its target KB. */
  async relinkJobToKb(jobId: string): Promise<{ kb_id: string; documents_in_session: number; newly_linked: number; message: string }> {
    const response = await apiClient.post(`/ops-retrieval/jobs/${jobId}/relink-kb`);
    return response.data;
  },

  /** List recent jobs for a sub-project. */
  async listJobs(subProjectId: string): Promise<OpsRetrievalJob[]> {
    const response = await apiClient.get<{ jobs: OpsRetrievalJob[] }>(
      `/ops-retrieval/sub-projects/${subProjectId}/jobs`
    );
    return response.data.jobs;
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Format seconds into a human-readable duration string. */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}
